import { MGrid, MSpinner, MSplitter } from "components/Shared";
import { MSectionProps } from "components/Shared/MSection";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import { EmployeeCustomerSurveyTypeMapDto, EmployeeListDto, QueueTypeEnum } from "interfaces/Mueller.Business.Models";
import React, { useEffect, useRef, useState } from "react";
import { getAccountListByQueueType } from "requests/employeeCustomerSurveyTypes/index";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { getEmployeesByRoles } from "requests/employee";
import {BUSOP_DIRECTOR, CUSTOMER_EXPERIENCE_DIRECTOR, CUSTOMER_EXPERIENCE_MANAGER} from "typeConstants/employeeRoles";
import {
  AUDIT_QA_QUEUE_MAPPING_VIEW,
  FIELD_APPOINTMENT_QUEUE_MAPPING_VIEW,
  HOMEOWNERS_QA_QUEUE_MAPPING_VIEW,
  PREFERRED_APPOINTMENT_QUEUE_MAPPING_VIEW,
  QUEUE_MAPPINGS_ALL_VIEW
} from "typeConstants/rights";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import { DATA_TYPE_NUMBER_UNFORMATTED } from "typeConstants/dataTypes";
import QueueMappingDetails from "./QueueMappingDetails";
import { ACTION_DOWNLOAD, FILTER_TYPE_TEXT, MGridColumnDefinition } from "components/Shared/MGrid";

interface QueueMappingsProps {
  //Redux
  makeErrorToast: (text: string) => void,
  canViewHomeownersQaMappings: boolean,
  canViewAuditQaMappings: boolean,
  canViewFieldAppointmentMappings: boolean,
  canViewPreferredAppointmentMappings: boolean,
  canViewAllMappings: boolean,
  profile: { firstName?: string, lastName?: string },
}

interface QueueTypeMapping {
  queueType: number,
  mappings: EmployeeCustomerSurveyTypeMapDto[],
  sectionName: string, //for MSection tabs
}

//Used to map the queues to the MSection titles
const sectionNameMappings = {
  [QueueTypeEnum.HomeownersQa]: "Homeowners QA",
  [QueueTypeEnum.AuditQa]: "Audit QA",
  [QueueTypeEnum.FieldAppointment]: "Field Appointment",
  [QueueTypeEnum.PreferredAppointment]: "Preferred Appointment",
};

const QueueMappings = (props: QueueMappingsProps) => {

  const [isFetchingAccountListsByQueueType, setIsFetchingAccountListsByQueueType] = useState<boolean>(true);
  const [queueTypeMappings, setQueueTypeMappings] = useState<QueueTypeMapping[]>([]);
  const [isFetchingEmployeesByRole, setIsFetchingEmployeesByRole] = useState<boolean>(true);
  const [employeeDropdownOptions, setEmployeeDropdownOptions] = useState<EmployeeListDto[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
  const [selectedQueueTypeMappingIndex, setSelectedQueueTypeMappingIndex] = useState<number>();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [employeeMenuAnchorEl, setEmployeeMenuAnchorEl] = useState<null | HTMLElement>(null);

  const mounted = useRef<boolean>(true);
  const sectionCollectionControlMethods = useRef();

  //Iterate through this to see which queues we need to attempt to fetch
  const viewingRights = {
    [QueueTypeEnum.HomeownersQa]: props.canViewHomeownersQaMappings || props.canViewAllMappings,
    [QueueTypeEnum.AuditQa]: props.canViewAuditQaMappings || props.canViewAllMappings,
    [QueueTypeEnum.FieldAppointment]: props.canViewFieldAppointmentMappings || props.canViewAllMappings,
    [QueueTypeEnum.PreferredAppointment]: props.canViewPreferredAppointmentMappings || props.canViewAllMappings,
  };

  const getQueueToFetchFirst = () => {
    if (props.canViewHomeownersQaMappings || props.canViewAllMappings) {
      return QueueTypeEnum.HomeownersQa;
    }
    else if (props.canViewAuditQaMappings) {
      return QueueTypeEnum.AuditQa;
    }
    else if (props.canViewFieldAppointmentMappings) {
      return QueueTypeEnum.FieldAppointment;
    }
    else if (props.canViewPreferredAppointmentMappings) {
      return QueueTypeEnum.PreferredAppointment;
    }
    else {
      return null;
    }
  };

  const getAccountListByQueueTypeFailedToaster = (queueName: string) => {
    props.makeErrorToast(`Failed to fetch queue info for ${queueName}`);
  };

  const getDownloadFileName = (queueSectionName: string) => {
    let firstName: string;
    let lastName: string;

    if (selectedEmployeeId) {
      const employee = employeeDropdownOptions.find(qad => qad.employeeId === selectedEmployeeId);
      firstName = employee?.firstName ?? "";
      lastName = employee?.lastName ?? "";
    } else {
      firstName = props.profile.firstName ?? "";
      lastName = props.profile.lastName ?? "";
    }

    const queueName = queueSectionName.replace(/\s+/g, "_");
    return `${firstName}_${lastName}_${queueName}_Mappings`;
  };

  useEffect(() => {
    const employeeRolesToFetch = [];
    if (props.canViewAllMappings || props.canViewAuditQaMappings || props.canViewHomeownersQaMappings) {
      employeeRolesToFetch.push(BUSOP_DIRECTOR);
    }
    if (props.canViewAllMappings || props.canViewFieldAppointmentMappings || props.canViewPreferredAppointmentMappings) {
      employeeRolesToFetch.push(CUSTOMER_EXPERIENCE_DIRECTOR);
      employeeRolesToFetch.push(CUSTOMER_EXPERIENCE_MANAGER);
    }

    if (employeeRolesToFetch.length) {
      getEmployeesByRoles(
        employeeRolesToFetch,
        (response: EmployeeListDto[]) => {
          setEmployeeDropdownOptions(response);
          setIsFetchingEmployeesByRole(false);
        },
        mounted);
    }
    else
      setIsFetchingEmployeesByRole(false);

    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setIsFetchingAccountListsByQueueType(true);
    setQueueTypeMappings([]);

    if(sectionCollectionControlMethods.current) {
      //Switch to the first tab when we change to a different person in the drop down
      sectionCollectionControlMethods.current.switchTab(0);
    }

    const queueToFetchFirst = getQueueToFetchFirst();

    //We check for the right in getQueueToFetchFirst so we don't have to check for the right here. Fetch the default queue if we haven't fetched anything yet
    if (queueToFetchFirst) {
      const sectionName = sectionNameMappings[queueToFetchFirst];
      getAccountListByQueueType(
        queueToFetchFirst,
        selectedEmployeeId,
        (response) => {
          setQueueTypeMappings(_ => [{ queueType: queueToFetchFirst, mappings: response, sectionName: sectionName }]);
          setIsFetchingAccountListsByQueueType(false);
        },
        () => {
          getAccountListByQueueTypeFailedToaster(sectionName);
          setIsFetchingAccountListsByQueueType(false);
        },
        mounted
      );
    }
  }, [selectedEmployeeId]);

  //When someone selects a different employee in the dropdown
  const onSelectedEmployeeChange = (selectedIndex: number) => {
    setSelectedQueueTypeMappingIndex(null);
    setSelectedEmployeeId(employeeDropdownOptions[selectedIndex].employeeId);
  };

  const getEmployeesMenuItems = () => {
    return [
      {
        primaryText: "My Mappings",
        onClick: () => setSelectedEmployeeId(0),
      },
      ...employeeDropdownOptions.map((qad, index) => ({ primaryText: qad.firstLastName, onClick: () => { onSelectedEmployeeChange(index); } }))];
  };

  const getMSectionChildren = (queueTypeMapping: number) => {
    if (isFetchingAccountListsByQueueType)
        return <MSpinner />;

    const index = queueTypeMappings.findIndex(qtm => qtm.queueType === queueTypeMapping);
    //-1 is returned by findIndex if it doesn't find a match
    if (index === -1 || !queueTypeMappings[index]?.mappings || !queueTypeMappings[index].mappings.length) {
        return <p>This user does not have any mappings for this queue type</p>;
    }

    return (
      <MGrid
        data={queueTypeMappings[index].mappings}
        columnDefinitions={getColumnDefs(queueTypeMappings[index].queueType)}
        className="full-height"
        onRowClick={onGridRowClicked}
        headerText="Customer/Employee Mappings"
        downloadFileName={getDownloadFileName(queueTypeMappings[index].sectionName)}
        actions={[ACTION_DOWNLOAD]}
        cardStyle
      />
    );
  };

  const getMappingSections = (): MSectionProps[] => {

    const sections: MSectionProps[] = [];
    for (const right in viewingRights) {
      if (viewingRights[right]) {
        sections.push(
          {
            //@ts-ignore
            key: right,
            title: sectionNameMappings[right],
            fullHeight: true,
            children: getMSectionChildren(Number.parseInt(right)),
          }
        );
      }
    }

    return sections;
  };

  //use the queue type mapping to generate keys. without unique keys the grid doesn't render properly between tabs
  const getColumnDefs = (queueType: number): MGridColumnDefinition[] => {
    const columnWidthNums = 120;
    const textColumnWidths = 280;
    return [
      {
        key: `account-number-queue-type-${queueType}`,
        name: "Account #",
        dataSource: "customerNumber",
        dataType: DATA_TYPE_NUMBER_UNFORMATTED,
        width: columnWidthNums,
      },
      {
        key: `account-name-queue-type-${queueType}`,
        name: "Account Name",
        dataSource: "customerName",
        width: textColumnWidths,
      },
      {
        key: `survey-type-queue-type-${queueType}`,
        name: "Survey Type",
        dataSource: "surveyTypeName",
        width: textColumnWidths,
      },
      {
        key: `manager-${queueType}`,
        name: "Manager",
        dataSource: "manager",
        width: textColumnWidths,
        filterType: FILTER_TYPE_TEXT,
      },
      {
        key: `cases-in-queue-queue-type-${queueType}`,
        name: "Cases in Queue",
        dataSource: "caseCountInQueue",
        dataType: DATA_TYPE_NUMBER_UNFORMATTED,
        width: columnWidthNums,
      },
      {
        key: `agents-mapped-queue-type-${queueType}`,
        name: "Employees Mapped",
        dataSource: "usersMapped",
        dataType: DATA_TYPE_NUMBER_UNFORMATTED,
        width: columnWidthNums,
      },
      {
        key: `agents-active-queue-type-${queueType}`,
        name: "Employees Active",
        dataSource: "usersActiveMapped",
        dataType: DATA_TYPE_NUMBER_UNFORMATTED,
        width: columnWidthNums,
      }
    ];
  };

  const onGridRowClicked = (row) => {
    setSelectedQueueTypeMappingIndex(row);
  };

  const tabs = getMappingSections();

  const onMSectionCollectionTabChange = (newSelectedTabIndex: number) => {
    //The tab title is really the only way I can link the queue to fetch to the tab itself
    const tabTitle = tabs[newSelectedTabIndex].title;
    setSelectedTabIndex(newSelectedTabIndex);
    setSelectedQueueTypeMappingIndex(null);

    //If we already have the queue type mapping for this queue, no need to fetch it again when switching tabs
    if (!queueTypeMappings.find(qtm => qtm.sectionName === tabTitle)) {
      setIsFetchingAccountListsByQueueType(true);

      let queueToFetch: string;
      //Get the property name of the property of sectionNameMappings that maps to the selected tab title
      for (const property in sectionNameMappings) {
        if (sectionNameMappings[property] === tabTitle) {
          queueToFetch = property;
        }
      }

      getAccountListByQueueType(
        queueToFetch,
        selectedEmployeeId,
        (response) => {
          //Typescript doesn't like that the section title can be a react node, but we know it's a string here
          //@ts-ignore
          setQueueTypeMappings(qtms => [...qtms, { queueType: Number.parseInt(queueToFetch), mappings: response, sectionName: tabTitle }]);
          setIsFetchingAccountListsByQueueType(false);
        },
        () => {
          //@ts-ignore
          getAccountListByQueueTypeFailedToaster(tabTitle);
          setIsFetchingAccountListsByQueueType(false);
        },
        mounted
      );
    }
  };

  const firstChild =
    <>
      {employeeDropdownOptions &&
        <div className="customer-employee-mappings-employee-drop-down-button">
          <Button
            onClick={(e) => setEmployeeMenuAnchorEl(e.currentTarget)}
            endIcon={<Icon>arrow_drop_down</Icon>}
          >
            {selectedEmployeeId ? employeeDropdownOptions.find((qad) => qad.employeeId === selectedEmployeeId).firstLastName : "My Mappings"}
          </Button>
          <Menu
            anchorEl={employeeMenuAnchorEl}
            open={Boolean(employeeMenuAnchorEl)}
            onClose={() => setEmployeeMenuAnchorEl(null)}
          >
            <MenuItem onClick={() => { setSelectedEmployeeId(0); setEmployeeMenuAnchorEl(null); }}>My Mappings</MenuItem>
            {employeeDropdownOptions.map((qad, index) => (
              <MenuItem key={qad.employeeId} onClick={() => { onSelectedEmployeeChange(index); setEmployeeMenuAnchorEl(null); }}>
                {qad.firstLastName}
              </MenuItem>
            ))}
          </Menu>
        </div>
      }
      {tabs && tabs.length ?
        <MSectionCollection
          sections={tabs}
          defaultMode={MSECTION_MODE_TAB}
          tabModeCollapsible={false}
          disableModeChanging
          fullHeight
          onTabChange={onMSectionCollectionTabChange}
          getControlMethods={cm => sectionCollectionControlMethods.current = cm}
        />
        :
        <p>Selected user does not have any mappings</p>
      }
    </>;

  if (isFetchingEmployeesByRole)
    return <MSpinner />;

  const isRenderingSecondChild =
  selectedQueueTypeMappingIndex !== null && selectedQueueTypeMappingIndex !== undefined
  && queueTypeMappings && queueTypeMappings.length > 0;

  let selectedRowQueueType: number;
  let selectedRowQueue: EmployeeCustomerSurveyTypeMapDto;
  let qtm: QueueTypeMapping | undefined;

  if (isRenderingSecondChild) {
    //Find the queue mapping that corresponds to the selected tab by checking the title
    const selectedMapping = Object.keys(sectionNameMappings).find(key => sectionNameMappings[key] === tabs[selectedTabIndex].title);
    qtm = queueTypeMappings.find(q => q.queueType === Number.parseInt(selectedMapping));
    if (qtm) {
      selectedRowQueueType = qtm.queueType;
      selectedRowQueue = qtm.mappings[selectedQueueTypeMappingIndex];
    }
  }

  return (
    <div className="customer-employee-mappings-container">
      <MSplitter
        firstChild={firstChild}
        secondChild={
          isRenderingSecondChild &&
          <QueueMappingDetails
            queueType={selectedRowQueueType}
            employeeCustomerSurveyTypeMap={selectedRowQueue}
          />
        }
        secondChildTitle={isRenderingSecondChild &&
        {
          title: `${qtm.mappings[selectedQueueTypeMappingIndex].customerNumber} ${qtm.mappings[selectedQueueTypeMappingIndex].customerName} - ${qtm.mappings[selectedQueueTypeMappingIndex].surveyTypeName} Mappings`,
          buttons: [
            {
              icon: "close",
              onClick: () => { setSelectedQueueTypeMappingIndex(null); }
            }
          ]
        }}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  canViewHomeownersQaMappings: userHasRight(HOMEOWNERS_QA_QUEUE_MAPPING_VIEW)(state),
  canViewAuditQaMappings: userHasRight(AUDIT_QA_QUEUE_MAPPING_VIEW)(state),
  canViewFieldAppointmentMappings: userHasRight(FIELD_APPOINTMENT_QUEUE_MAPPING_VIEW)(state),
  canViewPreferredAppointmentMappings: userHasRight(PREFERRED_APPOINTMENT_QUEUE_MAPPING_VIEW)(state),
  canViewAllMappings: userHasRight(QUEUE_MAPPINGS_ALL_VIEW)(state),
  profile: state.user.profile,
});

const mapDispatchToProps = dispatch => ({
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(QueueMappings);
